import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { API_CONFIG, ApiConfig } from '../../api-config';
import { ApiResponse } from '@shared-models';

interface PageableData {
  content: any[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

@Injectable({ providedIn: 'root' })
export class BusinessActivityService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/settings/countries`;
  }

  private normalizeStatus(status: string | undefined): 'active' | 'inactive' {
    if (!status) return 'active';
    return status.toLowerCase() as 'active' | 'inactive';
  }

  /**
   * LISTA: Business activity titles (server-side pagination)
   * GET `/settings/countries/{id}/business-activities`
   */
  getBusinessActivities(
    countryId: string,
    page = 0,
    size = 20,
    sort = 'createdAt',
    direction: 'asc' | 'desc' = 'desc',
    search?: string,
    status?: string,
  ): Observable<BusinessActivityPageResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', sort)
      .set('direction', direction);

    if (search && search.trim()) {
      params = params.set('search', search.trim());
    }
    if (status && status.trim()) {
      params = params.set('status', status.trim().toUpperCase());
    }

    const url = `${this.apiUrl}/${countryId}/business-activities`;
    return this.httpClient.get<ApiResponse<PageableData>>(url, { params }).pipe(
      map((response) => {
        const pageableData = response.data;
        const items = pageableData?.content || [];
        return {
          items: items.map((it: any) => ({
            id: it.id,
            code: it.code || '',
            name: it.name || it.title || '',
            status: this.normalizeStatus(it.status),
            document: it.document ?? false,
            subject: it.subject ?? false,
            detail: it.detail ?? false,
            createdAt: it.createdAt || it.created_at || '',
          })) as BusinessActivity[],
          page: pageableData?.page ?? 0,
          size: pageableData?.size ?? size,
          totalElements: pageableData?.totalElements ?? 0,
          totalPages: pageableData?.totalPages ?? 0,
        } as BusinessActivityPageResponse;
      }),
    );
  }

  getBusinessActivityById(countryId: string, id: string | number) {
    const url = `${this.apiUrl}/${countryId}/business-activities/${id}`;
    return this.httpClient.get<ApiResponse<any>>(url).pipe(
      map((resp) => {
        const it = resp.data;
        return {
          id: it.id,
          code: it.code || '',
          name: it.name || it.title || '',
          status: this.normalizeStatus(it.status),
          document: it.document ?? false,
          subject: it.subject ?? false,
          detail: it.detail ?? false,
          createdAt: it.createdAt || it.created_at || '',
        } as BusinessActivity;
      }),
    );
  }

  createBusinessActivity(
    countryId: string,
    payload: { name?: string; title?: string; status?: string },
  ) {
    const url = `${this.apiUrl}/${countryId}/business-activities`;
    return this.httpClient
      .post<{ success: boolean; message: string }>(
        url,
        this.toApiPayload(payload),
      )
      .pipe(
        map((res) => ({
          message: res.message || 'Actividad de empresa creada',
        })),
      );
  }

  updateBusinessActivity(
    countryId: string,
    id: string | number,
    payload: { name?: string; title?: string; status?: string },
  ) {
    const url = `${this.apiUrl}/${countryId}/business-activities/${id}`;
    return this.httpClient
      .put<{ success: boolean; message: string }>(
        url,
        this.toApiPayload(payload),
      )
      .pipe(
        map((res) => ({
          message: res.message || 'Actividad de empresa actualizada',
        })),
      );
  }

  deleteBusinessActivity(countryId: string, id: string | number) {
    const url = `${this.apiUrl}/${countryId}/business-activities/${id}`;
    return this.httpClient
      .delete<{ success: boolean; message: string }>(url)
      .pipe(
        map((res) => ({
          message: res.message || 'Actividad de empresa eliminada',
        })),
      );
  }

  /**
   * API flatten: body usa name/prefix/code (+ flags opcionales).
   */
  private toApiPayload(payload: {
    name?: string;
    title?: string;
    code?: string;
    prefix?: string;
    status?: string;
    document?: boolean;
    subject?: boolean;
    detail?: boolean;
  }): Record<string, string | boolean> {
    const body: Record<string, string | boolean> = {
      name: (payload.name ?? payload.title ?? '').trim(),
    };
    const code = (payload.code ?? '').trim();
    if (code) body['code'] = code;
    const prefix = (payload.prefix ?? '').trim();
    if (prefix) body['prefix'] = prefix;
    if (payload.status != null && payload.status !== '') {
      body['status'] = String(payload.status).toUpperCase();
    }
    if (payload.document !== undefined) body['document'] = !!payload.document;
    if (payload.subject !== undefined) body['subject'] = !!payload.subject;
    if (payload.detail !== undefined) body['detail'] = !!payload.detail;
    return body;
  }
}

export interface BusinessActivity {
  id: string;
  code?: string;
  name: string;
  status?: string;
  document?: boolean;
  subject?: boolean;
  detail?: boolean;
  createdAt?: string;
}

export interface BusinessActivityPageResponse {
  items: BusinessActivity[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}
